/**
 * SHEIN 营销活动展示文案与格式化。
 */

import i18n from '@/i18n';

export const ACTIVITY_TYPE_LABELS = {
  1: '频道',
  2: '清仓',
  3: '大促',
  4: '日常',
};

export const AUDIT_STATUS_LABELS = {
  0: '审核中',
  1: '审核通过',
  2: '审核不通过',
  4: '取消报名',
  7: '活动暂停',
  8: '取消中',
};

export const PARTAKE_FIELD_LABELS = {
  1: '活动价格/降幅',
  2: '活动件数',
  3: '活动预留件数',
};

export const ADJUST_FORM_LABELS = {
  1: '活动价格',
  6: '售价降幅',
};

export const PARTAKE_SCOPE_LABELS = {
  0: 'SKC 报价',
  1: 'SKU 报价',
};

function firstText(...candidates) {
  for (const c of candidates) {
    if (c == null) continue;
    const s = String(c).trim();
    if (s) return s;
  }
  return '';
}

export function activityTypeLabel(code) {
  const n = Number(code);
  if (Number.isFinite(n) && ACTIVITY_TYPE_LABELS[n]) return i18n.t('marketing.sht' + n, { ns: 'workbench', defaultValue: ACTIVITY_TYPE_LABELS[n] });
  const raw = firstText(code);
  return raw || '—';
}

export function auditStatusLabel(code) {
  const n = Number(code);
  if (Number.isFinite(n) && AUDIT_STATUS_LABELS[n]) return i18n.t('marketing.sha' + n, { ns: 'workbench', defaultValue: AUDIT_STATUS_LABELS[n] });
  const raw = firstText(code);
  return raw || '—';
}

/** @returns {'pending'|'pass'|'fail'|'cancel'|'paused'|'neutral'} */
export function auditStatusTone(code) {
  const n = Number(code);
  if (n === 0 || n === 8) return 'pending';
  if (n === 1) return 'pass';
  if (n === 2) return 'fail';
  if (n === 4) return 'cancel';
  if (n === 7) return 'paused';
  return 'neutral';
}

export function partakeFieldLabels(list) {
  if (!Array.isArray(list) || !list.length) return [];
  return list.map((code) => {
    const n = Number(code);
    return PARTAKE_FIELD_LABELS[n]
      ? i18n.t('marketing.pf' + n, { ns: 'workbench', defaultValue: PARTAKE_FIELD_LABELS[n] })
      : i18n.t('marketing.fieldN', { ns: 'workbench', code, defaultValue: `字段 ${code}` });
  });
}

export function adjustFormLabel(code) {
  const n = Number(code);
  if (ADJUST_FORM_LABELS[n]) return i18n.t('marketing.af' + n, { ns: 'workbench', defaultValue: ADJUST_FORM_LABELS[n] });
  return code != null && code !== '' ? String(code) : '—';
}

export function partakeScopeLabel(code) {
  const n = Number(code);
  if (PARTAKE_SCOPE_LABELS[n]) return i18n.t('marketing.ps' + n, { ns: 'workbench', defaultValue: PARTAKE_SCOPE_LABELS[n] });
  return code != null && code !== '' ? String(code) : '—';
}

/** 毫秒时间戳 → 本地可读时间 */
export function formatMsTime(ms) {
  if (ms == null || ms === '') return '—';
  const n = Number(ms);
  if (!Number.isFinite(n) || n <= 0) return '—';
  try {
    return new Date(n).toLocaleString('zh-CN', { hour12: false });
  } catch {
    return String(ms);
  }
}

export function formatTimeRange(startMs, endMs) {
  const a = formatMsTime(startMs);
  const b = formatMsTime(endMs);
  if (a === '—' && b === '—') return '—';
  return `${a} ~ ${b}`;
}

export function formatPrice(value, currency) {
  if (value == null || value === '') return '—';
  const raw = String(value).trim();
  if (!raw) return '—';
  if (raw.startsWith('$') || raw.startsWith('¥') || raw.startsWith('€')) return raw;
  const cur = firstText(currency);
  if (!cur) return raw;
  if (cur === 'CNY' || cur === 'RMB') return `¥${raw}`;
  if (cur === 'USD') return `$${raw}`;
  return `${cur} ${raw}`;
}

/**
 * 报名记录价格摘要（自运营 siteInfoList / 半托管 supplyPriceInfo / SKU 列表）
 */
export function formatEnrollPriceSummary(row) {
  if (!row || typeof row !== 'object') return '—';

  const skuList = Array.isArray(row.skuInfoList) ? row.skuInfoList : [];
  if (skuList.length) {
    const parts = [];
    for (const sku of skuList.slice(0, 3)) {
      const siteBits = formatSiteInfoList(sku?.siteInfoList);
      const supplyBit = formatSupplyPriceInfo(sku?.supplyPriceInfo);
      const bit = siteBits || supplyBit;
      if (bit) parts.push(`${sku.sku || 'SKU'}: ${bit}`);
    }
    if (!parts.length) return '—';
    const more = skuList.length > 3 ? i18n.t('marketing.skuMore', { ns: 'workbench', count: skuList.length }) : '';
    return `${parts.join('；')}${more}`;
  }

  const siteBits = formatSiteInfoList(row.siteInfoList);
  if (siteBits) return siteBits;
  const supplyBit = formatSupplyPriceInfo(row.supplyPriceInfo);
  if (supplyBit) return supplyBit;
  return '—';
}

function formatSiteInfoList(list) {
  if (!Array.isArray(list) || !list.length) return '';
  return list
    .map((site) => {
      const name = firstText(site?.site) || i18n.t('marketing.siteFallback', { ns: 'workbench' });
      if (site?.enrollSalePriceRate != null && site.enrollSalePriceRate !== '') {
        return i18n.t('marketing.siteDrop', { ns: 'workbench', name, rate: site.enrollSalePriceRate });
      }
      if (site?.enrollPrice != null && site.enrollPrice !== '') {
        return `${name} ${formatPrice(site.enrollPrice, site.currency || site.enrollCurrency)}`;
      }
      return '';
    })
    .filter(Boolean)
    .join('、');
}

function formatSupplyPriceInfo(info) {
  if (!info || typeof info !== 'object') return '';
  if (info.enrollCostPrice != null && info.enrollCostPrice !== '') {
    return formatPrice(info.enrollCostPrice, info.currency || info.enrollCurrency);
  }
  return '';
}

export function canCancelEnroll(row) {
  return Number(row?.auditStatus) === 0;
}

/** 报名记录价格摘要最多直显的 SKU 行数，超出走「更多」弹窗 */
export const ENROLL_SKU_PREVIEW_LIMIT = 2;

/**
 * 报名记录「产品信息」列：标题 / 货号 / SPU。
 * 后端已把平台报文与本地商品库合并成 productTitle / supplierCode / spu，这里再做兜底解析。
 */
export function enrollProductInfo(row) {
  return {
    title: firstText(row?.productTitle, row?.productName, row?.goodsTitle, row?.spuTitle, row?.title),
    supplierCode: firstText(row?.supplierCode, row?.goodsSn, row?.skcSupplierCode),
    spu: firstText(row?.spu, row?.spuName, row?.spuCode),
    skc: firstText(row?.skc, row?.skcName, row?.skcCode),
    color: firstText(row?.colorLabel, row?.colorName),
    imageUrl: firstText(row?.skcImageUrl, row?.mainImageUrl, row?.imageUrl),
  };
}

/**
 * 报名记录价格明细：一个 SKU 一行。
 * 平台按 SKU 报价时用 skuInfoList；按 SKC 报价时退化成一行（sku 为空）。
 */
export function enrollSkuPriceRows(row) {
  if (!row || typeof row !== 'object') return [];
  const skuList = Array.isArray(row.skuInfoList) ? row.skuInfoList : [];
  if (skuList.length) {
    return skuList.map((sku, idx) => {
      const code = firstText(sku?.sku, sku?.skuId) || `SKU ${idx + 1}`;
      const text = formatSiteInfoList(sku?.siteInfoList)
        || formatSupplyPriceInfo(sku?.supplyPriceInfo)
        || '—';
      return { key: `${code}-${idx}`, sku: code, priceText: text };
    });
  }
  const text = formatSiteInfoList(row.siteInfoList)
    || formatSupplyPriceInfo(row.supplyPriceInfo)
    || '—';
  return [{ key: 'skc-price', sku: '—', priceText: text }];
}

/**
 * 报名记录按 SPU 分组：同一 SPU 下的多个 SKC 合并为一个展示块。
 * - 分组键优先 SPU，缺失时退回 SKC（保证每行都有归属，不丢数据）
 * - 组内产品信息取各行非空字段的并集（部分 SKC 未命中本地库时也能补齐标题/货号）
 */
export function groupEnrollRowsBySpu(rows) {
  const groups = [];
  const indexByKey = new Map();
  (Array.isArray(rows) ? rows : []).forEach((row, index) => {
    const info = enrollProductInfo(row);
    const key = info.spu || info.skc || `enroll-${index}`;
    let group = indexByKey.get(key);
    if (!group) {
      group = { key, product: { ...info }, items: [] };
      indexByKey.set(key, group);
      groups.push(group);
    }
    const product = group.product;
    product.title = product.title || info.title;
    product.supplierCode = product.supplierCode || info.supplierCode;
    product.spu = product.spu || info.spu;
    product.color = product.color || info.color;
    product.imageUrl = product.imageUrl || info.imageUrl;
    group.items.push({ row, index });
  });
  return groups;
}
